
import React, { useState } from 'react';
import { X, Sparkles, Building2, Smartphone, AlignLeft, Check, Loader2, Phone } from 'lucide-react';
import { AgentProfile } from '../types';
import { supabase } from '../supabase';

interface WelcomeModalProps {
  agent: AgentProfile;
  onClose: () => void;
  onUpdate: (updated: Partial<AgentProfile>) => void;
}

export const WelcomeModal: React.FC<WelcomeModalProps> = ({ agent, onClose, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    business_name: agent.business_name || '',
    phone: agent.phone || '',
    whatsapp: agent.whatsapp || '',
    bio: agent.bio || ''
  });

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase) return;
    setLoading(true);

    try {
      const { error } = await supabase
        .from('agents')
        .update(formData)
        .eq('id', agent.id);

      if (error) throw error;

      onUpdate(formData);
      onClose();
    } catch (err) {
      console.error("Failed to update welcome data:", err);
      alert("Something went wrong, but you can update your profile later in Settings.");
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/90 backdrop-blur-2xl animate-in fade-in duration-500 overflow-y-auto">
      <div className="bg-white dark:bg-zinc-950 w-full max-w-md rounded-[40px] overflow-hidden shadow-2xl relative animate-in zoom-in-95 duration-500 my-auto">
        
        {/* Decorative Header */}
        <div className="h-32 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 flex items-center justify-center relative overflow-hidden shrink-0">
          <div className="absolute inset-0 opacity-20">
             <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white via-transparent to-transparent scale-150" />
          </div>
          <div className="relative flex flex-col items-center text-center">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center mb-2 shadow-xl border border-white/30">
               <Sparkles className="w-7 h-7 text-white fill-white" />
            </div>
          </div>
        </div>

        <div className="p-8 pt-6">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-black text-gray-900 dark:text-white tracking-tighter uppercase leading-none mb-2">Welcome to Findily</h2>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em]">Let's launch your professional brand</p>
          </div>

          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-1">
              <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest ml-1">Agency Brand Name</label>
              <div className="relative">
                <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input 
                  required
                  type="text"
                  placeholder="e.g. Sterling Estates"
                  className="w-full pl-12 pr-4 py-3.5 bg-gray-50 dark:bg-zinc-900 dark:text-white rounded-2xl text-sm font-bold outline-none border-2 border-transparent focus:border-blue-500 transition-all"
                  value={formData.business_name}
                  onChange={e => setFormData({...formData, business_name: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest ml-1">Direct Call Line</label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input 
                    required
                    type="tel"
                    placeholder="234..."
                    className="w-full pl-12 pr-4 py-3.5 bg-gray-50 dark:bg-zinc-900 dark:text-white rounded-2xl text-sm font-bold outline-none border-2 border-transparent focus:border-blue-500 transition-all"
                    value={formData.phone}
                    onChange={e => setFormData({...formData, phone: e.target.value})}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest ml-1">WhatsApp Number</label>
                <div className="relative">
                  <Smartphone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input 
                    required
                    type="tel"
                    placeholder="234..."
                    className="w-full pl-12 pr-4 py-3.5 bg-gray-50 dark:bg-zinc-900 dark:text-white rounded-2xl text-sm font-bold outline-none border-2 border-transparent focus:border-blue-500 transition-all"
                    value={formData.whatsapp}
                    onChange={e => setFormData({...formData, whatsapp: e.target.value})}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest ml-1">Professional Bio</label>
              <div className="relative">
                <AlignLeft className="absolute left-4 top-4 w-4 h-4 text-gray-400" />
                <textarea 
                  required
                  rows={2}
                  placeholder="What makes your agency special?"
                  className="w-full pl-12 pr-4 py-3.5 bg-gray-50 dark:bg-zinc-900 dark:text-white rounded-2xl text-sm font-medium outline-none border-2 border-transparent focus:border-blue-500 transition-all resize-none"
                  value={formData.bio}
                  onChange={e => setFormData({...formData, bio: e.target.value})}
                />
              </div>
            </div>

            <div className="pt-2 space-y-2">
              <button 
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-blue-600 text-white rounded-[20px] font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-blue-500/20 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Check className="w-5 h-5" /> Save & Get Started</>}
              </button>
              <button 
                type="button"
                onClick={onClose}
                className="w-full py-2 text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-gray-600 dark:hover:text-zinc-200 transition-colors"
              >
                Skip for now
              </button>
            </div>
          </form>
        </div>

        <div className="px-8 pb-6 text-center shrink-0">
           <p className="text-[8px] font-medium text-gray-400 leading-relaxed max-w-[280px] mx-auto">
             Set up your identity for buyers. You can refine these details anytime in Profile Settings.
           </p>
        </div>
      </div>
    </div>
  );
};
